"use client";

import AddToFav from "@/modules/add-to-fav/add-to-fav";
import ModalTemplate from "@/components/molecules/modal-template";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { ChevronDown, HeartIcon, Loader2, Minus, Plus, ShoppingCart } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useState } from "react";
import { Button, buttonVariants } from "@/components/atoms/buttons";
import { __, _x, getPermalink } from "@/lib/i18n/functions";
import { Locales } from "@/lib/i18n";
import { useAddToCart } from "@/hooks/useCartMutations";
import { Toast, toast } from "@/components/atoms/toast";
import { useCart } from "@/hooks/useCart";
import { formatProductQuantity } from "@/lib/product-quantity-label";
import { useProductQuantity } from "@/hooks/useProductQuantity";
import { sanitizeImageUrl } from "@/components/molecules/image-gallery";
import { formatPrice } from "@/lib/price";
import { useWishlist } from "@/hooks/useWishlist";
import { PriceToastDropdown } from "@/components/atoms/price-toast-dropdown";
import {
  getCartQuantityStep,
  normalizeConditioning,
  toLotQuantity,
  toPieceQuantity,
  toLotPrice,
  toPiecePrice,
} from "@/lib/product-units";

export type ProductCardProps = {
  productName: string;
  productPrice: number;
  productImage: string;
  productTag: string;
  reference: string;
  variant?: "default" | "image-only";
  link: string;
  className?: string;
  productId: string;
  medusaId: string;
  medusaVariantId: string;
  locale: Locales;
  isPending?: boolean;
  isCollection?: boolean;
  onQuantityChange?: (quantity: number) => void;
  productConditioning?: number | string;
  isOutOfStock?: boolean;
  isRange?: boolean;
  isPriceDynamic?: boolean;
  forSale?: boolean;
};

const ProductCard = ({
  productImage,
  productTag,
  productName,
  reference,
  productPrice,
  variant = "default",
  medusaVariantId,
  link,
  className,
  locale,
  isPending,
  isCollection = false,
  productConditioning = 1,
  onQuantityChange,
  medusaId,
  isOutOfStock = false,
  isRange = false,
  isPriceDynamic = false,
  forSale = false,
}: ProductCardProps) => {
  const quantityStep = getCartQuantityStep(productConditioning);
  const physicalPieceCount = normalizeConditioning(productConditioning);
  const [count, setCount] = useProductQuantity(productConditioning);
  const [inputValue, setInputValue] = useState(toLotQuantity(count, productConditioning).toString());
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (!isEditing) {
      setInputValue(toLotQuantity(count, productConditioning).toString());
    }
  }, [count, isEditing, productConditioning]);

  const { lists, isProductInAnyList, isLoading } = useWishlist();
  const isWishlisted = isProductInAnyList(medusaId);
  const [showListDropdown, setShowListDropdown] = useState(isWishlisted);
  const [openModal, setOpenModal] = useState(false);

  useEffect(() => setShowListDropdown(isWishlisted), [isWishlisted]);

  const price = formatPrice(toLotPrice(productPrice, productConditioning), { showDecimals: true });

  const { cart } = useCart();
  const cartItem = cart?.items && cart?.items.find((item) => item.variant_id === medusaVariantId);
  const isInCart = Boolean(cartItem);
  const cartQuantity = toLotQuantity(cartItem?.quantity || 0, productConditioning).toString();

  const formattedPricePerPiece = toPiecePrice(productPrice, productConditioning);

  const { addToCart, isLoading: isAddingToCart } = useAddToCart({
    onSuccess: () => {
      toast.success({ title: __("Item added to cart!", locale) });
    },
    onError: (error) => {
      console.error("Failed to add to cart:", error);
      toast.error({ title: __("Failed to add item to cart", locale) });
    },
  });

  // Notify parent component when quantity changes
  useEffect(() => {
    onQuantityChange?.(count);
  }, [count, onQuantityChange]);

  const handleWishlistClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!isWishlisted) {
      setOpenModal(true);
    } else {
      setShowListDropdown(true);
    }
  };

  const incrementCount = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setCount(count + quantityStep);
  };

  const decrementCount = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (count > quantityStep) {
      setCount(count - quantityStep);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === "" || /^\d+$/.test(value)) {
      setInputValue(value);
    }
  };

  const handleInputBlur = () => {
    setIsEditing(false);
    const numValue = parseInt(inputValue, 10) || 1;
    const clampedValue = toPieceQuantity(numValue, productConditioning);
    setCount(clampedValue);
    setInputValue(toLotQuantity(clampedValue, productConditioning).toString());
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.currentTarget.blur();
    } else if (e.key === "Escape") {
      setInputValue(toLotQuantity(count, productConditioning).toString());
      setIsEditing(false);
      e.currentTarget.blur();
    }
  };

  const handleInputFocus = () => {
    setIsEditing(true);
    setTimeout(() => {
      const input = document.activeElement as HTMLInputElement;
      if (input) input.select();
    }, 0);
  };

  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    addToCart(medusaVariantId, count);
  };

  return (
    <article className={cn("group/card relative w-full rounded-md", className)}>
      <Link
        href={link}
        className="focus-visible:ring-dark-primary/80 absolute inset-0 z-10 rounded-md focus:outline-none focus-visible:ring-[3px] focus-visible:ring-offset-1"
        aria-label={_x("View the product {{ productName }}", { productName }, locale)}
      >
        <span className="sr-only">View the product : {productName}</span>
      </Link>

      <div>
        <figure className="bg-product-card relative flex h-[15.625rem] w-full items-center justify-center gap-2 overflow-hidden rounded-md md:h-[22.625rem]">
          {variant === "default" ? (
            <div
              aria-label={_x("Product image of {{ productName }}", { productName }, locale)}
              className="relative flex size-[9.625rem] items-center justify-center md:h-[85%] md:w-[85%]"
            >
              {!isPending &&
                (productImage ? (
                  <Image
                    src={sanitizeImageUrl(productImage)}
                    fill
                    className="absolute object-contain mix-blend-multiply outline-offset-2 transition-transform duration-300 group-hover/card:scale-105"
                    alt={_x("Product image of {{ productName }}", { productName }, locale)}
                  />
                ) : (
                  <span className="text-xs opacity-50">{__("No Image", locale)}</span>
                ))}
            </div>
          ) : (
            <div className="relative flex size-full items-center justify-center">
              {!isPending &&
                (productImage ? (
                  <Image
                    src={sanitizeImageUrl(productImage)}
                    fill
                    className="absolute object-cover transition-transform duration-300 group-hover/card:scale-105"
                    alt={_x("Product image of {{ productName }}", { productName }, locale)}
                  />
                ) : (
                  <span className="text-xs opacity-50">No Image</span>
                ))}
            </div>
          )}

          {showListDropdown && (
            <ModalTemplate
              title={__("Please select your list", locale)}
              className="w-full sm:max-w-[506px]"
              classNameTitle="text-lg font-normal"
              triggerAsChild
              modalTrigger={
                <button
                  className={cn(
                    "text-dark-primary bg-white-primary peer/wishlist absolute top-1 right-1 left-1 flex h-8 cursor-pointer items-center justify-between rounded-sm px-1 py-2 md:top-2 md:right-2.5 md:left-2.5 md:h-12.5 md:px-3",
                    "animate-fadeIn z-20"
                  )}
                  aria-label={__("Select wishlist for this product", locale)}
                >
                  <div className="flex items-center gap-2">
                    <HeartIcon
                      className="fill-pink-primary stroke-pink-primary h-5 w-5.5 md:size-8"
                      aria-hidden="true"
                    />
                    <span className="text-[10px] font-medium md:text-sm">{__("Select a list", locale)}</span>
                  </div>
                  <ChevronDown size={18} aria-hidden="true" />
                </button>
              }
            >
              <AddToFav locale={locale} productId={medusaId} />
            </ModalTemplate>
          )}
          {openModal && (
            <ModalTemplate
              title={__("Please select your list", locale)}
              className="w-full sm:max-w-[506px]"
              classNameTitle="text-lg font-normal"
              triggerAsChild={false}
              open={openModal}
              onOpenChange={(open) => {
                setOpenModal(open);
              }}
              modalTrigger={""}
            >
              <AddToFav locale={locale} productId={medusaId} showForm={!lists?.length} />
            </ModalTemplate>
          )}

          {!showListDropdown && !isCollection && (
            <>
              {isPending || isLoading ? (
                <div className="absolute top-3 left-2.5 z-20 md:top-3.5 md:left-4">
                  <div className="shimmer h-5 w-5.5 rounded bg-[#eaeaea]! opacity-50 md:h-8 md:w-8" />
                </div>
              ) : (
                <>
                  {variant === "default" ? (
                    <button
                      onClick={handleWishlistClick}
                      className="group peer absolute top-3 left-2.5 z-20 cursor-pointer transition-transform duration-150 ease-in-out hover:scale-105 focus:scale-105 active:scale-95 md:top-3.5 md:left-4"
                      aria-label={_x("Add {{ productName }} to wishlist", { productName }, locale)}
                    >
                      <HeartIcon
                        strokeWidth={1}
                        aria-hidden="true"
                        className="text-dark-primary hover:text-pink-primary group-focus:text-pink-primary hover:fill-pink-primary group-focus:fill-pink-primary h-5 w-5.5 md:size-8"
                      />
                    </button>
                  ) : (
                    <button
                      onClick={handleWishlistClick}
                      className="group peer absolute top-2.5 left-3 z-20 flex size-7 cursor-pointer items-center justify-center rounded-md bg-black/17 transition-transform duration-150 ease-in-out hover:scale-105 focus:scale-105 active:scale-95 md:size-10.5"
                      aria-label={_x("Add {{ productName }} to wishlist", { productName }, locale)}
                    >
                      <HeartIcon
                        strokeWidth={1}
                        aria-hidden="true"
                        className="hover:text-pink-primary hover:fill-pink-primary group-focus:text-pink-primary group-focus:fill-pink-primary text-white-primary h-5 w-5.5 focus:scale-95 md:size-8"
                      />
                    </button>
                  )}
                </>
              )}
            </>
          )}
          {!showListDropdown && productTag && (
            <Badge className="text-dark-primary absolute top-3 right-2.5 z-20 max-w-9/12 rounded-xs bg-white px-[2.5px] py-[0.5px] text-[8px] leading-[15px] font-medium uppercase sm:max-w-max md:top-6 md:right-2.5 md:text-[10px]">
              {productTag}
            </Badge>
          )}
          {!isPending && !isCollection && !isRange && (
            <div
              className={cn(
                "absolute right-1 bottom-1 left-1 z-20 opacity-0 transition-opacity duration-300 ease-in group-focus-within/card:opacity-100 group-hover/card:opacity-100 group-focus/card:opacity-100 peer-focus:opacity-100 peer-focus/wishlist:opacity-100 has-focus:opacity-100 md:right-2.5 md:bottom-2 md:left-2.5",
                !isOutOfStock &&
                  "bg-white-primary flex h-8 items-center justify-between gap-1.5 rounded-sm px-1 py-1 md:h-12.5 md:gap-2 md:rounded-md md:px-2 md:py-1.25"
              )}
            >
              {isOutOfStock ? (
                <Toast
                  id={`out-${medusaId}`}
                  type="warning"
                  className="min-[384px]:min-w-none mb-0.5 w-full max-w-none shadow-none max-sm:p-2"
                  titleClassName="max-sm:text-[10px]"
                  title={__("This product is out of stock", locale)}
                />
              ) : isInCart ? (
                // Show "In Cart" state with quantity and go to cart button
                <>
                  <div className="flex items-center gap-1 text-[10px] md:text-sm">
                    <ShoppingCart size={12} className="text-green-600 md:size-4" aria-hidden="true" />
                    {locale !== "nl" && (
                      <span className="font-medium text-green-600 max-sm:hidden">
                        {_x("In cart ({{ cartQuantity }})", { cartQuantity }, locale)}
                      </span>
                    )}
                    <span className={cn("font-medium text-green-600 sm:hidden", locale === "nl" && "sm:block")}>
                      {cartQuantity}
                    </span>
                  </div>
                  <Link
                    href={getPermalink(locale, "cart")}
                    aria-label={__("Go to cart", locale)}
                    className={cn(
                      buttonVariants(),
                      "flex h-6.5 items-center justify-center rounded-xs p-0 px-2 text-[10px] transition-transform duration-150 ease-in-out active:scale-95 md:h-10 md:px-4 md:text-sm"
                    )}
                  >
                    {__("View Cart", locale)}
                  </Link>
                </>
              ) : (
                // Show regular add to cart controls
                <>
                  <span className="text-[10px] md:text-sm">QTY</span>
                  <div className="bg-product-card flex h-[26px] grow items-center justify-between rounded-sm md:h-10">
                    <button
                      onClick={decrementCount}
                      className="group flex h-full w-6 cursor-pointer items-center justify-center md:w-8"
                      aria-label={__("Decrease Quantity", locale)}
                    >
                      <Minus
                        size={12}
                        className={cn(
                          count <= quantityStep ? "text-primary-light/50" : "text-primary",
                          "group-focus:text-dark-primary"
                        )}
                        aria-hidden="true"
                      />
                    </button>
                    <div className="flex h-full min-w-4 grow items-center justify-center">
                      <input
                        size={1}
                        type="text"
                        value={inputValue}
                        onChange={handleInputChange}
                        onBlur={handleInputBlur}
                        onKeyDown={handleInputKeyDown}
                        onFocus={handleInputFocus}
                        aria-live="polite"
                        aria-atomic="true"
                        aria-label={__("Quantity", locale)}
                        className="w-full border-none bg-transparent p-0 text-center text-xs font-medium tabular-nums outline-none focus:ring-0 md:text-base"
                        min={1}
                      />
                    </div>
                    <button
                      onClick={incrementCount}
                      className="group flex h-full w-6 cursor-pointer items-center justify-center md:w-8"
                      aria-label={__("Increase Quantity", locale)}
                    >
                      <Plus size={12} className={"text-primary group-focus:text-dark-primary"} aria-hidden="true" />
                    </button>
                  </div>
                  <Button
                    onClick={handleAddToCart}
                    disabled={isAddingToCart}
                    aria-label={_x("Add {{ quantity }} of {{ productName }} to cart", {
                      quantity: formatProductQuantity(count, productConditioning, locale), productName,
                    }, locale)}
                    className={cn(
                      "flex h-6.5 w-11 items-center justify-center rounded-xs p-0 text-[10px] transition-transform duration-150 ease-in-out active:scale-95 md:h-10 md:w-[4.625rem] md:text-sm",
                      locale === "nl" && "w-16 md:w-[6rem]"
                    )}
                  >
                    {isAddingToCart ? <Loader2 aria-hidden="true" className="animate-spin" /> : __("Add", locale)}
                  </Button>
                </>
              )}
            </div>
          )}
        </figure>
        <div className="w-full space-y-2 pt-3 pb-1 md:px-2.5">
          <div className="flex w-full items-start justify-between gap-1.25 font-medium">
            <h5 className="text-sm md:line-clamp-2 md:text-xl">{productName}</h5>
            {isRange || isPriceDynamic ? (
              <PriceToastDropdown locale={locale} />
            ) : (
              !isCollection &&
              (isPending ? (
                <span className="shimmer inline-block h-8 w-12 rounded" />
              ) : (
                <span className="text-sm font-medium md:text-xl" aria-label={`Price: ${price}`}>
                  {price}
                </span>
              ))
            )}
          </div>
          {!isCollection && (
            <dl className="text-primary-light flex items-center justify-between gap-1.25">
              <div className="flex items-center gap-1.25">
                {physicalPieceCount > 1 && (
                  <>
                    <dt className="sr-only">Price per piece</dt>
                    <dd className="text-xs capitalize md:text-sm">
                      {formatPrice(formattedPricePerPiece, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                      /{__("piece", locale)}
                    </dd>
                  </>
                )}
                <dt className="sr-only">Reference</dt>{" "}
                {isPending ? (
                  <span className="shimmer hidden h-[10px] w-16 rounded md:inline-block" />
                ) : (
                  <dd
                    className="hidden text-xs md:inline-block md:text-sm"
                    aria-label={_x("Reference number: {{ reference }}", { reference }, locale)}
                  >
                    {physicalPieceCount > 1 && "·"} {__("REF", locale)} : {reference}
                  </dd>
                )}
                {forSale && (
                  <>
                    <dt className="sr-only">Product Type</dt>
                    <dd>
                      <Badge variant="festi">{__("To sell", locale)}</Badge>
                    </dd>
                  </>
                )}
              </div>
              {physicalPieceCount > 1 && (
                <>
                  <dt className="sr-only">Quantity</dt>
                  <dd>
                    <Badge className="rounded-2.5xl bg-white-secondary text-blue-light px-2 py-1.5 text-right text-xs leading-3.5 font-medium whitespace-normal">
                      {formatProductQuantity(count, productConditioning, locale)}
                    </Badge>
                  </dd>
                </>
              )}
            </dl>
          )}
        </div>
      </div>
    </article>
  );
};

export default ProductCard;
